/**
 * Tree Builder Composable
 *
 * Builds complete tree node structure for Q-tree component visualization.
 * Handles full BRICK Schema hierarchy:
 * - Site (root)
 *   - Buildings
 *     - Levels
 *       - Spaces
 *         - Equipment
 *           - Zone Sections (for AHUs)
 *             - Child Equipment
 *               - Devices
 *                 - Component Devices (for compound devices)
 *                   - Points
 *           - Devices
 *             - Component Devices
 *               - Points
 *           - Points (direct)
 *       - Zones (Electrical/HVAC)
 *         - Equipment
 *           - Devices
 *             - Points
 *
 * This function was extracted from UnifiedBrickSchemaManager.vue to improve maintainability.
 * Original function was 897 lines - the largest in the entire component (7% of file).
 *
 * @module composables/brick-schema/visualization/useTreeBuilder
 */

export function useTreeBuilder() {
  /**
   * Build complete tree node structure for Q-tree component
   *
   * Node Structure:
   * - id: Unique identifier (e.g., "equipment-123")
   * - label: Display name
   * - icon: Material icon name
   * - color: Quasar color name
   * - type: Node type (site, building, level, space, equipment, device, point, etc.)
   * - data: Original data object
   * - brickClass: BRICK Schema class
   * - children: Array of child nodes
   * - Additional metadata (templateId, isCompound, etc.)
   *
   * Icon/Color Assignments:
   * - Equipment: Based on BRICK class (Transformer, Switchboard, DDC_Panel, etc.)
   * - Devices: 'memory' (standard), 'hub' (compound), 'place' (remote)
   * - Points: 'sensors' or 'lens'
   * - Template-based equipment: Orange color
   * - Compound equipment: Purple color
   *
   * Point Deduplication:
   * - Tracks point IDs shown under devices to avoid duplicates
   * - Only shows direct equipment/zone points if not already under a device
   *
   * @param {Object} hierarchyData - Full hierarchy data from API
   * @param {Object} site - Site information
   * @param {Object} expandedNodes - Ref to expanded nodes array
   * @param {Object} preservingTreeState - Ref to tree state preservation flag
   * @returns {Array} Tree nodes for Q-tree component
   */
  const buildTreeNodes = (hierarchyData, site, expandedNodes, preservingTreeState) => {
    if (!hierarchyData?.buildings) {
      return [];
    }

    // Add site as root node
    const siteNode = {
      id: `site-${site.id}`,
      label: site.name,
      icon: 'business',
      color: 'primary',
      type: 'site',
      brickClass: site.brick_class || 'brick:Site',
      children: [],
    };

    // Add buildings
    if (hierarchyData?.buildings) {
      hierarchyData.buildings.forEach((building) => {
        const buildingNode = {
          id: `building-${building.id}`,
          label: building.name,
          icon: 'apartment',
          color: 'secondary',
          type: 'building',
          data: building,
          brickClass: building.brick_class,
          children: [],
        };

        // Add levels
        if (building.levels) {
          building.levels.forEach((level) => {
            const levelNode = {
              id: `level-${level.id}`,
              label: level.name,
              icon: 'layers',
              color: 'info',
              type: 'level',
              data: level,
              brickClass: level.brick_class,
              children: [],
            };

            // Add spaces
            if (level.spaces) {
              level.spaces.forEach((space) => {
                const spaceNode = {
                  id: `space-${space.id}`,
                  label: space.name,
                  icon: 'room',
                  color: 'accent',
                  type: 'space',
                  data: space,
                  brickClass: space.brick_class,
                  children: [],
                };

                // Add equipment - Only show equipment at space level if it has no parent
                // Equipment with parent_equipment_id should only appear under their parent
                if (space.equipment) {
                  space.equipment.filter((equipment) => !equipment.parent_equipment_id).forEach((equipment) => {
                    const brickClass = equipment.computed_brick_class || equipment.brick_class || '';
                    let icon = 'precision_manufacturing';
                    let color = (equipment.equipment_template_id || equipment.template_id) ? 'orange' : 'positive';

                    // Debug template protection for AHU1
                    if (equipment.id === 105) {
                    }

                    if (equipment.is_compound) {
                      icon = 'hub';
                      color = 'purple';
                    } else if (brickClass.includes('Transformer')) {
                      icon = 'electrical_services';
                      color = 'deep-purple-6';
                    } else if (brickClass.includes('Main_Switchboard')) {
                      icon = 'dashboard';
                      color = 'red-6';
                    } else if (brickClass.includes('Mechanical_Services_Switchboard')) {
                      icon = 'settings_input_component';
                      color = 'orange-6';
                    } else if (brickClass.includes('Distribution_Board')) {
                      icon = 'view_module';
                      color = 'blue-6';
                    } else if (brickClass.includes('DDC_Panel')) {
                      icon = 'developer_board';
                      color = 'yellow-8';
                    } else if (brickClass.includes('Switchboard')) {
                      icon = 'electrical_services';
                      color = 'grey-8';
                    } else if (brickClass.includes('Generator')) {
                      icon = 'power';
                      color = 'orange-8';
                    } else if (brickClass.includes('UPS')) {
                      icon = 'battery_charging_full';
                      color = 'purple-6';
                    }

                    // Build label with controller assignment indicator
                    let equipmentLabel = equipment.name;
                    if (equipment.assigned_controller_id) {
                      equipmentLabel += ' 🎮'; // Controller badge
                    }
                    if (equipment.controller_template_id && equipment.assigned_equipment && equipment.assigned_equipment.length > 0) {
                      equipmentLabel += ` (${equipment.assigned_equipment.length})`; // Count of assigned equipment
                    }

                    // Determine if this is a switchboard type
                    const isSwitchboard = brickClass.includes('Switchboard') ||
                                         brickClass.includes('Distribution_Board') ||
                                         brickClass.includes('DDC_Panel');

                    const equipmentNode = {
                      id: `equipment-${equipment.id}`,
                      label: equipmentLabel,
                      icon: icon,
                      color: color,
                      type: isSwitchboard ? 'switchboard' : 'equipment',
                      data: equipment,
                      brickClass: brickClass,
                      // Template fields - provide multiple aliases for frontend compatibility
                      equipment_template_id: equipment.equipment_template_id,
                      template_id: equipment.equipment_template_id || equipment.template_id,
                      templateId: equipment.equipment_template_id || equipment.template_id,
                      templateName: equipment.template_name,
                      isCompound: equipment.is_compound || false,
                      componentCount: equipment.component_count || 0,
                      // Controller assignment fields
                      assigned_controller_id: equipment.assigned_controller_id,
                      controller_template_id: equipment.controller_template_id,
                      controller_template_name: equipment.controller_template_name,
                      assigned_equipment_count: equipment.assigned_equipment?.length || 0,
                      children: [],
                    };

                    // Add zone sections for AHUs (internal mechanical zones)
                    if (equipment.zone_sections && Array.isArray(equipment.zone_sections) && equipment.zone_sections.length > 0) {
                      equipment.zone_sections.forEach((zone) => {
                        const zoneNode = {
                          id: `zone-${zone.id}`,
                          label: zone.name,
                          icon: 'dashboard_customize',
                          color: 'purple',
                          type: 'zoneSection',  // Changed to zoneSection to distinguish it
                          subtype: 'zone_section',
                          data: zone,
                          brickClass: zone.brick_class || 'AHU_Zone_Section',
                          children: [],
                        };

                        // Add child equipment (like dampers) under zone section if any
                        if (zone.child_equipment && Array.isArray(zone.child_equipment) && zone.child_equipment.length > 0) {
                          zone.child_equipment.forEach((childEquip) => {
                            const childNode = {
                              id: `equipment-${childEquip.id}`,
                              label: childEquip.name,
                              icon: childEquip.brick_class?.includes('Damper') ? 'air' : 'precision_manufacturing',
                              color: 'accent',
                              type: 'equipment',
                              data: childEquip,
                              brickClass: childEquip.brick_class,
                              children: [],
                            };

                            // Track device point IDs to exclude them from direct points
                            const childEquipDevicePointIds = new Set();

                            // Add devices under child equipment (excluding component devices)
                            if (childEquip.devices && Array.isArray(childEquip.devices) && childEquip.devices.length > 0) {
                            // Filter out devices that are components of compound devices
                              const standaloneDevices = childEquip.devices.filter((d) => !d.parent_compound_id);
                              standaloneDevices.forEach((device) => {
                              // Build compound device label with proper component count
                                let deviceLabel = device.name;
                                if (device.is_compound) {
                                  const componentCount = device.components?.length || device.component_count || 0;
                                  if (componentCount > 0 && device.components && Array.isArray(device.components)) {
                                  // Show parent device name with component names
                                    const componentNames = device.components.map((c) => c.name).join(' + ');
                                    deviceLabel = `${device.name} + ${componentNames}`;
                                    if (componentCount > 2) {
                                    // If more than 2 components, show count
                                      const firstTwo = device.components.slice(0, 2).map((c) => c.name).join(' + ');
                                      deviceLabel = `${device.name} + ${firstTwo} [${componentCount} components]`;
                                    }
                                  } else if (componentCount > 0) {
                                    deviceLabel = `${device.name} [${componentCount} components]`;
                                  } else {
                                    deviceLabel = `${device.name} [Compound]`;
                                  }
                                }

                                const deviceNode = {
                                  id: `device-${device.id}`,
                                  label: deviceLabel,
                                  icon: device.is_compound ? 'hub' : 'memory',
                                  color: device.is_compound ? 'purple' : (device.template_id ? 'orange' : 'secondary'),
                                  type: 'device',
                                  data: device,
                                  brickClass: device.brick_class,
                                  isCompound: device.is_compound || false,
                                  componentCount: device.components?.length || device.component_count || 0,
                                  tooltip: device.is_compound ? `Compound device with ${device.components?.length || device.component_count || 0} components` : null,
                                  parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                                  parentEquipmentTemplateName: equipment.template_name,
                                  children: [],
                                };

                                // Add component devices if this is a compound device
                                if (device.is_compound && device.components && Array.isArray(device.components)) {
                                  device.components.forEach((component) => {
                                    let componentLabel = component.name;
                                    const componentDetails = [];
                                    if (component.manufacturer) {componentDetails.push(component.manufacturer);}
                                    if (component.model_number) {componentDetails.push(component.model_number);}
                                    if (componentDetails.length > 0) {
                                      componentLabel = `${component.name} (${componentDetails.join(' - ')})`;
                                    }

                                    const componentNode = {
                                      id: `device-${component.id}`,
                                      label: componentLabel,
                                      icon: 'settings_input_component',
                                      color: 'accent',
                                      type: 'device',
                                      data: component,
                                      brickClass: component.computed_brick_class || component.brick_class,
                                      isComponent: true,
                                      parentCompoundId: device.id,
                                      parentEquipmentTemplateId: childEquip.equipment_template_id || childEquip.template_id,
                                      parentEquipmentTemplateName: childEquip.template_name,
                                      children: [],
                                      selectable: true,
                                    };

                                    // Add points under component device
                                    if (component.points && Array.isArray(component.points)) {
                                      component.points.forEach((point) => {
                                        childEquipDevicePointIds.add(point.id);
                                        const pointNode = {
                                          id: `point-${point.id}`,
                                          label: point.name,
                                          icon: 'sensors',
                                          color: 'info',
                                          type: 'point',
                                          data: point,
                                          brickClass: point.computed_brick_class || point.brick_class,
                                          parentDeviceTemplateId: component.device_template_id || component.template_id,
                                          parentEquipmentTemplateId: childEquip.equipment_template_id || childEquip.template_id,
                                          parentEquipmentTemplateName: childEquip.template_name,
                                        };
                                        componentNode.children.push(pointNode);
                                      });
                                    }

                                    deviceNode.children.push(componentNode);
                                  });
                                }

                                // Add points under device
                                if (device.points && Array.isArray(device.points) && device.points.length > 0) {
                                  device.points.forEach((point) => {
                                    childEquipDevicePointIds.add(point.id);
                                    const pointNode = {
                                      id: `point-${point.id}`,
                                      label: point.name,
                                      icon: 'sensors',
                                      color: 'info',
                                      type: 'point',
                                      data: point,
                                      brickClass: point.brick_class,
                                      parentDeviceTemplateId: device.device_template_id || device.template_id,
                                      parentEquipmentTemplateId: childEquip.equipment_template_id || childEquip.template_id,
                                      parentEquipmentTemplateName: childEquip.template_name,
                                    };
                                    deviceNode.children.push(pointNode);
                                  });
                                }

                                childNode.children.push(deviceNode);
                              });
                            }

                            // Add direct points under child equipment (only those without device_id or not in device points)
                            if (childEquip.points && Array.isArray(childEquip.points) && childEquip.points.length > 0) {
                            // Filter to only show points that don't belong to a device
                              const directPoints = childEquip.points.filter((point) =>
                                !point.device_id && !childEquipDevicePointIds.has(point.id),
                              );

                              directPoints.forEach((point) => {
                                const pointNode = {
                                  id: `point-${point.id}`,
                                  label: point.name,
                                  icon: 'sensors',
                                  color: 'info',
                                  type: 'point',
                                  data: point,
                                  brickClass: point.brick_class,
                                  parentEquipmentTemplateId: childEquip.equipment_template_id || childEquip.template_id,
                                  parentEquipmentTemplateName: childEquip.template_name,
                                };
                                childNode.children.push(pointNode);
                              });
                            }

                            zoneNode.children.push(childNode);
                          });
                        }

                        // Collect all device point IDs first
                        const devicePointIds = new Set();

                        // Add devices under zone section if any
                        if (zone.name === 'Zone 1') {
                        }
                        if (zone.devices && Array.isArray(zone.devices) && zone.devices.length > 0) {
                        // Filter out devices that are components of compound devices
                          const standaloneDevices = zone.devices.filter((d) => !d.parent_compound_id);
                          standaloneDevices.forEach((device) => {
                          // Build compound device label with proper component count
                            let deviceLabel = device.name;
                            if (device.is_compound) {
                              const componentCount = device.components?.length || device.component_count || 0;
                              if (componentCount > 0 && device.components && Array.isArray(device.components)) {
                              // Show parent device name with component names
                                const componentNames = device.components.map((c) => c.name).join(' + ');
                                deviceLabel = `${device.name} + ${componentNames}`;
                                if (componentCount > 2) {
                                // If more than 2 components, show count
                                  const firstTwo = device.components.slice(0, 2).map((c) => c.name).join(' + ');
                                  deviceLabel = `${device.name} + ${firstTwo} [${componentCount} components]`;
                                }
                              } else if (componentCount > 0) {
                                deviceLabel = `${device.name} [${componentCount} components]`;
                              } else {
                                deviceLabel = `${device.name} [Compound]`;
                              }
                            }

                            const deviceNode = {
                              id: `device-${device.id}`,
                              label: deviceLabel,
                              icon: device.is_compound ? 'hub' : 'memory',
                              color: device.is_compound ? 'purple' : (device.template_id ? 'orange' : 'secondary'),
                              type: 'device',
                              data: device,
                              brickClass: device.brick_class,
                              isCompound: device.is_compound || false,
                              componentCount: device.components?.length || device.component_count || 0,
                              tooltip: device.is_compound ? `Compound device with ${device.components?.length || device.component_count || 0} components` : null,
                              parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                              parentEquipmentTemplateName: equipment.template_name,
                              children: [],
                            };

                            // Add component devices if this is a compound device
                            if (device.is_compound && device.components && Array.isArray(device.components)) {
                              device.components.forEach((component) => {
                                let componentLabel = component.name;
                                const componentDetails = [];
                                if (component.manufacturer) {componentDetails.push(component.manufacturer);}
                                if (component.model_number) {componentDetails.push(component.model_number);}
                                if (componentDetails.length > 0) {
                                  componentLabel = `${component.name} (${componentDetails.join(' - ')})`;
                                }

                                const componentNode = {
                                  id: `device-${component.id}`,
                                  label: componentLabel,
                                  icon: 'settings_input_component',
                                  color: 'accent',
                                  type: 'device',
                                  data: component,
                                  brickClass: component.computed_brick_class || component.brick_class,
                                  isComponent: true,
                                  parentCompoundId: device.id,
                                  parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                                  parentEquipmentTemplateName: equipment.template_name,
                                  children: [],
                                  selectable: true,
                                };

                                // Add points under component device
                                if (component.points && Array.isArray(component.points)) {
                                  component.points.forEach((point) => {
                                    devicePointIds.add(point.id);
                                    const pointNode = {
                                      id: `point-${point.id}`,
                                      label: point.name,
                                      icon: 'sensors',
                                      color: 'info',
                                      type: 'point',
                                      data: point,
                                      brickClass: point.computed_brick_class || point.brick_class,
                                      parentDeviceTemplateId: component.device_template_id || component.template_id,
                                      parentEquipmentTemplateId: zone.equipment_template_id || zone.template_id,
                                      parentEquipmentTemplateName: zone.template_name,
                                    };
                                    componentNode.children.push(pointNode);
                                  });
                                }

                                deviceNode.children.push(componentNode);
                              });
                            }

                            // Add points under device and collect their IDs
                            if (device.points && Array.isArray(device.points) && device.points.length > 0) {
                              device.points.forEach((point) => {
                              // Track this point ID to exclude from zone's direct points
                                devicePointIds.add(point.id);

                                const pointNode = {
                                  id: `point-${point.id}`,
                                  label: point.name,
                                  icon: 'sensors',
                                  color: 'info',
                                  type: 'point',
                                  data: point,
                                  brickClass: point.brick_class,
                                  parentDeviceTemplateId: device.device_template_id || device.template_id,
                                  parentEquipmentTemplateId: zone.equipment_template_id || zone.template_id,
                                  parentEquipmentTemplateName: zone.template_name,
                                };
                                deviceNode.children.push(pointNode);
                              });
                            }

                            zoneNode.children.push(deviceNode);
                          });
                        }

                        // Add direct points under zone section (excluding those already shown under devices)
                        if (zone.points && Array.isArray(zone.points) && zone.points.length > 0) {
                        // Filter to only show points that aren't already displayed under a device
                          const directPoints = zone.points.filter((point) => !devicePointIds.has(point.id));

                          directPoints.forEach((point) => {
                            const pointNode = {
                              id: `point-${point.id}`,
                              label: point.name,
                              icon: 'sensors',
                              color: 'info',
                              type: 'point',
                              data: point,
                              brickClass: point.brick_class,
                              parentEquipmentTemplateId: zone.equipment_template_id || zone.template_id,
                              parentEquipmentTemplateName: zone.template_name,
                            };
                            zoneNode.children.push(pointNode);
                          });
                        }

                        equipmentNode.children.push(zoneNode);
                      });
                    }

                    // Collect all device point IDs first
                    const equipmentDevicePointIds = new Set();

                    // Add devices under equipment (excluding component devices)
                    if (equipment.devices && Array.isArray(equipment.devices) && equipment.devices.length > 0) {
                    // Filter out devices that are components of compound devices
                      const standaloneDevices = equipment.devices.filter((d) => !d.parent_compound_id);
                      standaloneDevices.forEach((device) => {
                      // Build device label with manufacturer/model and location if available
                        let deviceLabel = device.name;
                        const details = [];
                        if (device.manufacturer) {details.push(device.manufacturer);}
                        if (device.model_number) {details.push(device.model_number);}
                        if (details.length > 0) {
                          deviceLabel = `${device.name} (${details.join(' - ')})`;
                        }

                        // Add location indicator if device is remotely located
                        if (device.is_remote && device.location_space_name) {
                          deviceLabel = `${deviceLabel} 📍 @ ${device.location_space_name}`;
                        }

                        // Build compound device label with proper component count
                        if (device.is_compound) {
                          const componentCount = device.components?.length || device.component_count || 0;
                          if (componentCount > 0 && device.components && Array.isArray(device.components)) {
                          // Show parent device name with component names
                            const componentNames = device.components.map((c) => c.name).join(' + ');
                            deviceLabel = `${device.name} + ${componentNames}`;
                            if (componentCount > 2) {
                            // If more than 2 components, show count
                              const firstTwo = device.components.slice(0, 2).map((c) => c.name).join(' + ');
                              deviceLabel = `${device.name} + ${firstTwo} [${componentCount} components]`;
                            }
                          } else if (componentCount > 0) {
                            deviceLabel = `${device.name} [${componentCount} components]`;
                          } else {
                            deviceLabel = `${device.name} [Compound]`;
                          }
                        }

                        const deviceNode = {
                          id: `device-${device.id}`,
                          label: deviceLabel,
                          icon: device.is_compound ? 'hub' : (device.is_remote ? 'place' : 'memory'),
                          color: device.is_compound ? 'purple' : (device.is_remote ? 'warning' : (device.template_id ? 'orange' : 'secondary')),
                          type: 'device',
                          data: device,
                          brickClass: device.computed_brick_class || device.brick_class,
                          templateId: device.template_id,
                          templateName: device.template_name,
                          manufacturer: device.manufacturer,
                          modelNumber: device.model_number,
                          isCompound: device.is_compound || false,
                          componentCount: device.components?.length || device.component_count || 0,
                          tooltip: device.is_compound ?
                            `Compound device with ${device.components?.length || device.component_count || 0} components` :
                            (device.location_display || null),
                          parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                          parentEquipmentTemplateName: equipment.template_name,
                          children: [],
                          selectable: true,
                        };

                        // Add component devices if this is a compound device
                        if (device.is_compound && device.components && Array.isArray(device.components)) {
                          device.components.forEach((component) => {
                            let componentLabel = component.name;
                            const componentDetails = [];
                            if (component.manufacturer) {componentDetails.push(component.manufacturer);}
                            if (component.model_number) {componentDetails.push(component.model_number);}
                            if (componentDetails.length > 0) {
                              componentLabel = `${component.name} (${componentDetails.join(' - ')})`;
                            }

                            const componentNode = {
                              id: `device-${component.id}`,
                              label: componentLabel,
                              icon: 'settings_input_component',
                              color: 'accent',
                              type: 'device',
                              data: component,
                              brickClass: component.computed_brick_class || component.brick_class,
                              isComponent: true,
                              parentCompoundId: device.id,
                              parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                              parentEquipmentTemplateName: equipment.template_name,
                              children: [],
                              selectable: true,
                            };

                            // Add points under component device
                            if (component.points && Array.isArray(component.points)) {
                              component.points.forEach((point) => {
                                equipmentDevicePointIds.add(point.id);
                                const pointNode = {
                                  id: `point-${point.id}`,
                                  label: point.name,
                                  icon: 'lens',
                                  color: 'info',
                                  type: 'point',
                                  data: point,
                                  brickClass: point.computed_brick_class || point.brick_class,
                                  parentDeviceTemplateId: component.device_template_id || component.template_id,
                                  parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                                  parentEquipmentTemplateName: equipment.template_name,
                                  children: [],
                                  selectable: false,
                                };
                                componentNode.children.push(pointNode);
                              });
                            }

                            deviceNode.children.push(componentNode);
                          });
                        }

                        // Add points under devices and collect their IDs
                        if (device.points && Array.isArray(device.points) && device.points.length > 0) {
                          device.points.forEach((point) => {
                          // Track this point ID to exclude from equipment's direct points
                            equipmentDevicePointIds.add(point.id);

                            const pointNode = {
                              id: `point-${point.id}`,
                              label: point.name,
                              icon: 'lens',
                              color: 'info',
                              type: 'point',
                              data: point,
                              brickClass: point.computed_brick_class || point.brick_class,
                              parentDeviceTemplateId: device.device_template_id || device.template_id,
                              parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                              parentEquipmentTemplateName: equipment.template_name,
                              children: [],
                              selectable: false,
                            };

                            deviceNode.children.push(pointNode);
                          });
                        }

                        equipmentNode.children.push(deviceNode);
                      });
                    }

                    // Add direct points under equipment (excluding those already shown under devices)
                    if (equipment.points && Array.isArray(equipment.points)) {
                    // Filter to only show points that aren't already displayed under a device
                      const directPoints = equipment.points.filter((point) => !equipmentDevicePointIds.has(point.id));

                      directPoints.forEach((point) => {
                        const pointNode = {
                          id: `point-${point.id}`,
                          label: point.name,
                          icon: 'sensors',
                          color: 'info',
                          type: 'point',
                          data: point,
                          brickClass: point.brick_class,
                          parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                          parentEquipmentTemplateName: equipment.template_name,
                          children: [],
                        };

                        equipmentNode.children.push(pointNode);
                      });
                    }

                    // Add child equipment (controllers, etc.) under this equipment
                    if (equipment.childEquipment && Array.isArray(equipment.childEquipment) && equipment.childEquipment.length > 0) {
                      equipment.childEquipment.forEach((childEquip) => {
                        const childBrickClass = childEquip.computed_brick_class || childEquip.brick_class || '';
                        let childIcon = 'precision_manufacturing';
                        let childColor = (childEquip.equipment_template_id || childEquip.template_id || childEquip.controller_template_id) ? 'orange' : 'positive';

                        // Icon/color for controllers
                        if (childBrickClass.includes('BMS_Controller') || childBrickClass.includes('Controller')) {
                          childIcon = 'settings_remote';
                          childColor = 'blue-7';
                        } else if (childBrickClass.includes('DDC_Panel')) {
                          childIcon = 'developer_board';
                          childColor = 'yellow-8';
                        }

                        // Determine if child equipment is a switchboard type
                        const isChildSwitchboard = childBrickClass.includes('Switchboard') ||
                                                   childBrickClass.includes('Distribution_Board') ||
                                                   childBrickClass.includes('DDC_Panel');

                        const childEquipNode = {
                          id: `equipment-${childEquip.id}`,
                          label: childEquip.name,
                          icon: childIcon,
                          color: childColor,
                          type: isChildSwitchboard ? 'switchboard' : 'equipment',
                          data: childEquip,
                          brickClass: childBrickClass,
                          equipment_template_id: childEquip.equipment_template_id,
                          controller_template_id: childEquip.controller_template_id,
                          template_id: childEquip.equipment_template_id || childEquip.template_id || childEquip.controller_template_id,
                          templateId: childEquip.equipment_template_id || childEquip.template_id || childEquip.controller_template_id,
                          templateName: childEquip.template_name,
                          children: [],
                        };

                        // Add points under child equipment if any
                        if (childEquip.points && Array.isArray(childEquip.points)) {
                          childEquip.points.forEach((point) => {
                            const pointNode = {
                              id: `point-${point.id}`,
                              label: point.name,
                              icon: 'sensors',
                              color: 'info',
                              type: 'point',
                              data: point,
                              brickClass: point.brick_class,
                              children: [],
                            };
                            childEquipNode.children.push(pointNode);
                          });
                        }

                        equipmentNode.children.push(childEquipNode);
                      });
                    }

                    spaceNode.children.push(equipmentNode);
                  });
                }

                levelNode.children.push(spaceNode);
              });
            }

            // Process zones at this level
            if (level.zones && Array.isArray(level.zones)) {
              level.zones.forEach((zone) => {
                const zoneNode = {
                  id: `zone-${zone.id}`,
                  label: zone.name || 'Unnamed Zone',
                  type: 'zone',
                  data: {
                    ...zone,
                    metadata: {
                      ...(zone.metadata || {}),
                      zone_type: zone.metadata?.zone_type || zone.properties?.zone_type || 'HVAC_Zone',
                    },
                    properties: {
                      ...(zone.properties || {}),
                      zone_type: zone.metadata?.zone_type || zone.properties?.zone_type || 'HVAC_Zone',
                    },
                    zone_type: zone.metadata?.zone_type || zone.properties?.zone_type || 'HVAC_Zone',
                  },
                  icon: (zone.metadata?.zone_type === 'Electrical_Zone' || zone.name?.includes('Electrical')) ? 'electrical_services' : 'air',
                  iconColor: zone.metadata?.zone_type === 'Electrical_Zone' ? 'amber' : 'light-blue',
                  color: zone.metadata?.zone_type === 'Electrical_Zone' ? 'amber' : 'info',
                  brickClass: zone.brick_class || 'brick:Zone',
                  children: [],
                  expandable: true,
                  selectable: true,
                };

                // Process equipment in this zone
                if (zone.equipment && Array.isArray(zone.equipment)) {
                  zone.equipment.forEach((equipment) => {
                    const brickClass = equipment.computed_brick_class || equipment.brick_class || '';
                    let icon = 'precision_manufacturing';
                    let color = equipment.template_id ? 'orange' : 'positive';

                    if (equipment.is_compound) {
                      icon = 'hub';
                      color = 'purple';
                    } else if (brickClass.includes('Transformer')) {
                      icon = 'electrical_services';
                      color = 'deep-purple-6';
                    } else if (brickClass.includes('Main_Switchboard')) {
                      icon = 'dashboard';
                      color = 'red-6';
                    } else if (brickClass.includes('Mechanical_Services_Switchboard')) {
                      icon = 'settings_input_component';
                      color = 'orange-6';
                    } else if (brickClass.includes('Distribution_Board')) {
                      icon = 'view_module';
                      color = 'blue-6';
                    } else if (brickClass.includes('DDC_Panel')) {
                      icon = 'developer_board';
                      color = 'yellow-8';
                    } else if (brickClass.includes('Switchboard')) {
                      icon = 'electrical_services';
                      color = 'grey-8';
                    } else if (brickClass.includes('Generator')) {
                      icon = 'power';
                      color = 'orange-8';
                    } else if (brickClass.includes('UPS')) {
                      icon = 'battery_charging_full';
                      color = 'purple-6';
                    }

                    // Build label with controller assignment indicator
                    let equipmentLabel = equipment.name;
                    if (equipment.assigned_controller_id) {
                      equipmentLabel += ' 🎮'; // Controller badge
                    }
                    if (equipment.controller_template_id && equipment.assigned_equipment && equipment.assigned_equipment.length > 0) {
                      equipmentLabel += ` (${equipment.assigned_equipment.length})`; // Count of assigned equipment
                    }

                    // Determine if this is a switchboard type
                    const isSwitchboard = brickClass.includes('Switchboard') ||
                                         brickClass.includes('Distribution_Board') ||
                                         brickClass.includes('DDC_Panel');

                    const equipmentNode = {
                      id: `equipment-${equipment.id}`,
                      label: equipmentLabel,
                      icon: icon,
                      color: color,
                      type: isSwitchboard ? 'switchboard' : 'equipment',
                      data: equipment,
                      brickClass: brickClass,
                      templateId: equipment.template_id,
                      templateName: equipment.template_name,
                      isCompound: equipment.is_compound || false,
                      componentCount: equipment.component_count || 0,
                      // Controller assignment fields
                      assigned_controller_id: equipment.assigned_controller_id,
                      controller_template_id: equipment.controller_template_id,
                      controller_template_name: equipment.controller_template_name,
                      assigned_equipment_count: equipment.assigned_equipment?.length || 0,
                      children: [],
                      selectable: true,
                    };

                    // Collect all device point IDs first
                    const equipmentDevicePointIds = new Set();

                    // Add devices under equipment (excluding component devices)
                    if (equipment.devices && Array.isArray(equipment.devices)) {
                    // Filter out devices that are components of compound devices
                      const standaloneDevices = equipment.devices.filter((d) => !d.parent_compound_id);
                      standaloneDevices.forEach((device) => {
                      // Build device label with manufacturer/model
                        let deviceLabel = device.name;
                        const details = [];
                        if (device.manufacturer) {details.push(device.manufacturer);}
                        if (device.model_number) {details.push(device.model_number);}
                        if (details.length > 0) {
                          deviceLabel = `${device.name} (${details.join(' - ')})`;
                        }

                        // Build compound device label with proper component count
                        if (device.is_compound) {
                          const componentCount = device.components?.length || device.component_count || 0;
                          if (componentCount > 0 && device.components && Array.isArray(device.components)) {
                          // Show parent device name with component names
                            const componentNames = device.components.map((c) => c.name).join(' + ');
                            deviceLabel = `${device.name} + ${componentNames}`;
                            if (componentCount > 2) {
                            // If more than 2 components, show count
                              const firstTwo = device.components.slice(0, 2).map((c) => c.name).join(' + ');
                              deviceLabel = `${device.name} + ${firstTwo} [${componentCount} components]`;
                            }
                          } else if (componentCount > 0) {
                            deviceLabel = `${device.name} [${componentCount} components]`;
                          } else {
                            deviceLabel = `${device.name} [Compound]`;
                          }
                        }

                        const deviceNode = {
                          id: `device-${device.id}`,
                          label: deviceLabel,
                          icon: device.is_compound ? 'hub' : 'memory',
                          color: device.is_compound ? 'purple' : (device.template_id ? 'orange' : 'secondary'),
                          type: 'device',
                          data: device,
                          brickClass: device.computed_brick_class || device.brick_class,
                          templateId: device.template_id,
                          templateName: device.template_name,
                          isCompound: device.is_compound || false,
                          componentCount: device.components?.length || device.component_count || 0,
                          tooltip: device.is_compound ? `Compound device with ${device.component_count || 0} components` : null,
                          parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                          parentEquipmentTemplateName: equipment.template_name,
                          children: [],
                          selectable: true,
                        };

                        // Add component devices if this is a compound device
                        if (device.is_compound && device.components && Array.isArray(device.components)) {
                          device.components.forEach((component) => {
                            let componentLabel = component.name;
                            const componentDetails = [];
                            if (component.manufacturer) {componentDetails.push(component.manufacturer);}
                            if (component.model_number) {componentDetails.push(component.model_number);}
                            if (componentDetails.length > 0) {
                              componentLabel = `${component.name} (${componentDetails.join(' - ')})`;
                            }

                            const componentNode = {
                              id: `device-${component.id}`,
                              label: componentLabel,
                              icon: 'settings_input_component',
                              color: 'accent',
                              type: 'device',
                              data: component,
                              brickClass: component.computed_brick_class || component.brick_class,
                              isComponent: true,
                              parentCompoundId: device.id,
                              parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                              parentEquipmentTemplateName: equipment.template_name,
                              children: [],
                              selectable: true,
                            };

                            // Add points under component device
                            if (component.points && Array.isArray(component.points)) {
                              component.points.forEach((point) => {
                                equipmentDevicePointIds.add(point.id);
                                const pointNode = {
                                  id: `point-${point.id}`,
                                  label: point.name,
                                  icon: 'lens',
                                  color: 'info',
                                  type: 'point',
                                  data: point,
                                  brickClass: point.computed_brick_class || point.brick_class,
                                  parentDeviceTemplateId: component.device_template_id || component.template_id,
                                  parentEquipmentTemplateId: equipment.equipment_template_id || equipment.template_id,
                                  parentEquipmentTemplateName: equipment.template_name,
                                  children: [],
                                  selectable: false,
                                };
                                componentNode.children.push(pointNode);
                              });
                            }

                            deviceNode.children.push(componentNode);
                          });
                        }

                        // Add points under devices and collect their IDs
                        if (device.points && Array.isArray(device.points)) {
                          device.points.forEach((point) => {
                            equipmentDevicePointIds.add(point.id);

                            const pointNode = {
                              id: `point-${point.id}`,
                              label: point.name,
                              icon: 'lens',
                              color: 'info',
                              type: 'point',
                              data: point,
                              brickClass: point.computed_brick_class || point.brick_class,
                              children: [],
                              selectable: false,
                            };

                            deviceNode.children.push(pointNode);
                          });
                        }

                        equipmentNode.children.push(deviceNode);
                      });
                    }

                    // Add direct points under equipment (excluding those already shown under devices)
                    if (equipment.points && Array.isArray(equipment.points)) {
                      const directPoints = equipment.points.filter((point) => !equipmentDevicePointIds.has(point.id));

                      directPoints.forEach((point) => {
                        const pointNode = {
                          id: `point-${point.id}`,
                          label: point.name,
                          icon: 'sensors',
                          color: 'info',
                          type: 'point',
                          data: point,
                          brickClass: point.brick_class,
                          children: [],
                          selectable: false,
                        };

                        equipmentNode.children.push(pointNode);
                      });
                    }

                    // Add child equipment (controllers, etc.) under this equipment
                    if (equipment.childEquipment && Array.isArray(equipment.childEquipment) && equipment.childEquipment.length > 0) {
                      equipment.childEquipment.forEach((childEquip) => {
                        const childBrickClass = childEquip.computed_brick_class || childEquip.brick_class || '';
                        let childIcon = 'precision_manufacturing';
                        let childColor = (childEquip.equipment_template_id || childEquip.template_id || childEquip.controller_template_id) ? 'orange' : 'positive';

                        // Icon/color for controllers
                        if (childBrickClass.includes('BMS_Controller') || childBrickClass.includes('Controller')) {
                          childIcon = 'settings_remote';
                          childColor = 'blue-7';
                        } else if (childBrickClass.includes('DDC_Panel')) {
                          childIcon = 'developer_board';
                          childColor = 'yellow-8';
                        }

                        // Determine if child equipment is a switchboard type
                        const isChildSwitchboard = childBrickClass.includes('Switchboard') ||
                                                   childBrickClass.includes('Distribution_Board') ||
                                                   childBrickClass.includes('DDC_Panel');

                        const childEquipNode = {
                          id: `equipment-${childEquip.id}`,
                          label: childEquip.name,
                          icon: childIcon,
                          color: childColor,
                          type: isChildSwitchboard ? 'switchboard' : 'equipment',
                          data: childEquip,
                          brickClass: childBrickClass,
                          equipment_template_id: childEquip.equipment_template_id,
                          controller_template_id: childEquip.controller_template_id,
                          template_id: childEquip.equipment_template_id || childEquip.template_id || childEquip.controller_template_id,
                          templateId: childEquip.equipment_template_id || childEquip.template_id || childEquip.controller_template_id,
                          templateName: childEquip.template_name,
                          children: [],
                          selectable: true,
                        };

                        // Add points under child equipment if any
                        if (childEquip.points && Array.isArray(childEquip.points)) {
                          childEquip.points.forEach((point) => {
                            const pointNode = {
                              id: `point-${point.id}`,
                              label: point.name,
                              icon: 'sensors',
                              color: 'info',
                              type: 'point',
                              data: point,
                              brickClass: point.brick_class,
                              children: [],
                              selectable: false,
                            };
                            childEquipNode.children.push(pointNode);
                          });
                        }

                        equipmentNode.children.push(childEquipNode);
                      });
                    }

                    zoneNode.children.push(equipmentNode);
                  });
                }

                levelNode.children.push(zoneNode);
              });
            }

            buildingNode.children.push(levelNode);
          });
        }

        siteNode.children.push(buildingNode);
      });
    }

    // DUAL-LOCATION FEATURE: Add reference nodes for devices with physical locations
    // Devices with location_space_id appear in TWO places:
    // 1. Primary node under their equipment (full functionality)
    // 2. Reference node under their physical space (visual indicator)

    // Collect all devices that have location_space_id
    const devicesWithPhysicalLocation = [];

    function collectDevicesWithLocation(node) {
      if (node.type === 'device' && node.data?.location_space_id) {
        // Get equipment's space_id to compare
        const equipmentSpaceId = node.data.equipment?.space_id;

        // Only add if device is NOT already in the same space as its equipment
        if (node.data.location_space_id !== equipmentSpaceId) {
          devicesWithPhysicalLocation.push({
            device: node.data,
            primaryNode: node,
          });
        }
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(collectDevicesWithLocation);
      }
    }

    // Traverse tree to collect devices
    collectDevicesWithLocation(siteNode);

    // Add reference nodes under spaces
    function addReferenceNodes(node) {
      if (node.type === 'space') {
        const spaceId = node.data.id;

        // Find devices that should appear in this space
        const devicesInSpace = devicesWithPhysicalLocation.filter((d) =>
          d.device.location_space_id === spaceId,
        );

        devicesInSpace.forEach(({ device, primaryNode }) => {
          // Create reference node
          const referenceNode = {
            id: `device-ref-${device.id}-in-space-${spaceId}`,
            label: `📍 ${device.name}`,
            type: 'device-reference',
            icon: 'place', // Material icon for location pin
            color: 'grey-6',
            data: {
              ...device,
              isReference: true,
              primaryDeviceId: device.id,
              primaryEquipmentId: device.site_equipment_id,
              primaryEquipmentName: device.equipment?.name || 'Unknown Equipment',
              locationDisplay: `Physical location of ${device.name} (Belongs to ${device.equipment?.name || 'Unknown Equipment'})`,
            },
            brickClass: device.computed_brick_class || device.brick_class,
            children: [],
            selectable: false,
            styleClass: 'device-reference-node',
          };

          // Add to space's children
          if (!node.children) {node.children = [];}
          node.children.push(referenceNode);
        });
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(addReferenceNodes);
      }
    }

    // Traverse tree again to add reference nodes
    addReferenceNodes(siteNode);

    // Smart initial expansion - only set if we're not preserving tree state
    // If we have existing expanded nodes but they're invalid (don't match current tree),
    // reset them to ensure the tree is visible
    const siteId = `site-${site.id}`;
    const hasValidExpansion = expandedNodes.value.includes(siteId);

    if (!preservingTreeState.value && (!hasValidExpansion || expandedNodes.value.length === 0)) {
      expandedNodes.value = [siteId]; // Always expand site node to show buildings

      // If only one building, also expand it to show levels
      if (hierarchyData?.buildings && hierarchyData.buildings.length === 1) {
        const building = hierarchyData.buildings[0];
        expandedNodes.value.push(`building-${building.id}`);
      }
      // If multiple buildings, only the site is expanded (buildings are visible but collapsed)
    }

    return [siteNode];
  };

  return {
    buildTreeNodes,
  };
}
